/**
 * Mein Profil — was jeder an sich selbst ändern darf.
 *
 * Selbst änderbar: Anzeigename, Vorname, Nachname. Die E-Mail geht nur über
 * eine Bestätigung per Link; Rolle, Chip und Station bleiben der Verwaltung
 * vorbehalten.
 *
 * Beim Leitstand-Umbau wurde ausschließlich die Darstellung getauscht:
 * Rahmen, Karte und Token-Farben (styles/index.css) statt fester Farbwerte.
 * Die Abläufe dahinter — E-Mail-Wechsel, Zwei-Faktor neu einrichten samt
 * Passwortabfrage — sind unverändert, Schritt für Schritt und Knopf für
 * Knopf. Das gilt bis in die Kleinigkeiten: Der Bestätigen-Knopf der
 * Zwei-Faktor-Abfrage behält seine Kennung `2fa-confirm-btn`, weil die
 * Eingabetaste im Passwortfeld genau ihn auslöst.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import { LeitstandFlaeche } from '../components/leitstand/LeitstandFlaeche';
import { Karte, Notiz } from '../components/leitstand';
import {
  updateProfile,
  changeEmail,
  disable2FA,
  reconfigure2FA,
  ApiException,
} from '../api/client';

const ROLE_LABELS: Record<string, string> = {
  WORKER: 'Mitarbeiter',
  TECHNICIAN: 'Techniker',
  ADMIN: 'Administrator',
  SUPERADMIN: 'Super-Administrator',
  SYSTEM: 'System',
};

const EINGABE =
  'w-full rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none disabled:opacity-60';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;
const FELD_MARKE = 'font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3';

/** Kürzel aus dem Namen: "Doreen Bergmann" → "DB" — wie in der Kopfleiste. */
function kuerzel(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0]?.toUpperCase() ?? '')
    .join('');
}

/** Eine Zeile der Übersicht: Beschriftung, Wert, rechts der zugehörige Weg. */
function Feld({ marke, wert, aktion }: { marke: string; wert: ReactNode; aktion?: ReactNode }) {
  return (
    <div className="flex items-center gap-4 border-b border-linie py-3 last:border-b-0">
      <div className="flex-1">
        <div className={FELD_MARKE}>{marke}</div>
        <div className="text-sm">{wert}</div>
      </div>
      {aktion}
    </div>
  );
}

/**
 * Der Fensterrahmen für die drei Abfragen. Verhalten wie zuvor: Klick neben
 * das Fenster schließt, solange nichts läuft — währenddessen ist zu.
 */
function Fenster({
  titel,
  schliessen,
  gesperrt = false,
  children,
  fuss,
}: {
  titel: string;
  schliessen: () => void;
  gesperrt?: boolean;
  children: ReactNode;
  fuss: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-grund/85 backdrop-blur-[2px]"
        onClick={() => !gesperrt && schliessen()}
      />
      <div className="relative w-full max-w-md rounded-leitstand border border-linie-2 bg-flaeche">
        <div className="flex items-center justify-between gap-3 border-b border-linie px-[18px] py-[14px]">
          <h2 className="text-sm font-semibold">{titel}</h2>
          <button
            onClick={() => !gesperrt && schliessen()}
            className="rounded-[4px] p-1 text-tinte-3 hover:text-tinte disabled:opacity-50"
            disabled={gesperrt}
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col gap-4 p-[18px]">{children}</div>
        <div className="flex justify-end gap-2 border-t border-linie px-[18px] py-[14px]">
          {fuss}
        </div>
      </div>
    </div>
  );
}

function Fehlerzeile({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-[4px] border border-stoer/40 bg-stoer/10 px-3 py-2 text-[13px] text-stoer">
      <AlertCircle className="h-4 w-4 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function Erfolgszeile({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-[4px] border border-lauf/40 bg-lauf/10 px-3 py-2 text-[13px] text-lauf">
      <CheckCircle className="h-4 w-4 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  // Fenster „Profil bearbeiten"
  const [editModalOpen, setEditModalOpen] = useState(false);
  // Fenster „Zwei-Faktor"
  const [twoFAModalOpen, setTwoFAModalOpen] = useState<'disable' | 'reconfigure' | null>(null);
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [twoFASaving, setTwoFASaving] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');

  // Fenster „E-Mail ändern"
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!user) {
    return (
      <LeitstandFlaeche>
        <div className="flex min-h-screen items-center justify-center">
          <p className="text-sm text-tinte-2">Nicht angemeldet</p>
        </div>
      </LeitstandFlaeche>
    );
  }

  const openEditModal = () => {
    setEditDisplayName(user.displayName);
    setEditFirstName(user.firstName || '');
    setEditLastName(user.lastName || '');
    setError('');
    setSuccess('');
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    // Prüfung im Browser, bevor der Server gefragt wird
    const trimmedDisplayName = editDisplayName.trim();
    if (!trimmedDisplayName) {
      setError('Anzeigename darf nicht leer sein');
      return;
    }
    if (trimmedDisplayName.length > 128) {
      setError('Anzeigename darf maximal 128 Zeichen haben');
      return;
    }

    setSaving(true);

    try {
      await updateProfile({
        displayName: trimmedDisplayName,
        firstName: editFirstName.trim() || undefined,
        lastName: editLastName.trim() || undefined,
      });

      setSuccess('Profil aktualisiert');

      // Benutzerdaten neu laden
      await refresh();

      // Fenster nach kurzer Verzögerung schließen
      setTimeout(() => {
        setEditModalOpen(false);
        setSuccess('');
      }, 1000);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error);
      } else {
        setError('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  const vollerName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  const leer = <span className="text-tinte-3">–</span>;

  return (
    <LeitstandRahmen titel="Mein Profil" benutzer={user.displayName}>
      <div className="flex max-w-[820px] flex-col gap-5">
        <Link to="/admin" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Zurück
        </Link>

        <Karte titel="Meine Angaben" marke="Anzeigename, Vor- und Nachname ändern Sie selbst">
          <div className="flex flex-col">
            <div className="flex items-center gap-4 border-b border-linie pb-4">
              <span className="grid h-14 w-14 flex-none place-items-center rounded-full bg-akzent-tief font-zahl text-lg font-semibold text-akzent">
                {kuerzel(user.displayName)}
              </span>
              <div className="flex-1">
                <div className="text-[17px] font-semibold">{user.displayName}</div>
                <div className="text-sm text-tinte-2">
                  {vollerName || ROLE_LABELS[user.role] || user.role}
                </div>
              </div>
              <button onClick={openEditModal} className={KNOPF_LEISE}>
                Bearbeiten
              </button>
            </div>

            <Feld marke="Vorname" wert={user.firstName || leer} />
            <Feld marke="Nachname" wert={user.lastName || leer} />

            <Feld
              marke="E-Mail"
              wert={user.email || leer}
              aktion={
                <button
                  onClick={() => {
                    setEditEmail(user.email || '');
                    setEmailError('');
                    setEmailSuccess('');
                    setEmailModalOpen(true);
                  }}
                  className={KNOPF_LEISE}
                >
                  Ändern
                </button>
              }
            />

            <Feld marke="Rolle" wert={ROLE_LABELS[user.role] || user.role} />

            <Feld
              marke="Zwei-Faktor-Anmeldung"
              wert={
                user.twoFactorEnabled ? (
                  <span className="text-lauf">Aktiviert</span>
                ) : (
                  <span className="text-stand">Nicht aktiviert</span>
                )
              }
              aktion={
                user.twoFactorEnabled ? (
                  <button
                    onClick={() => {
                      setTwoFAModalOpen('reconfigure');
                      setTwoFAPassword('');
                      setTwoFAError('');
                    }}
                    className={KNOPF_LEISE}
                  >
                    Neu einrichten
                  </button>
                ) : (
                  <Link to="/2fa/setup" className={KNOPF_LEISE}>
                    Einrichten
                  </Link>
                )
              }
            />

            {user.stationId && (
              <Feld marke="Aktive Station" wert={user.stationType || 'Unbekannt'} />
            )}
          </div>
        </Karte>

        <Notiz titel="Was Sie selbst ändern können">
          Anzeigename, Vor- und Nachname ändern Sie hier selbst. Eine neue E-Mail-Adresse wird erst
          wirksam, wenn Sie den Link in der Bestätigungsmail geöffnet haben. Rolle und Station
          ändert nur die Verwaltung.
        </Notiz>
      </div>

      {/* E-Mail ändern */}
      {emailModalOpen && (
        <Fenster
          titel="E-Mail ändern"
          gesperrt={emailSaving}
          schliessen={() => setEmailModalOpen(false)}
          fuss={
            <>
              <button
                onClick={() => setEmailModalOpen(false)}
                className={KNOPF_LEISE}
                disabled={emailSaving}
              >
                {emailSuccess ? 'Schließen' : 'Abbrechen'}
              </button>
              {!emailSuccess && (
                <button
                  onClick={async () => {
                    setEmailError('');
                    setEmailSaving(true);
                    try {
                      await changeEmail(editEmail);
                      setEmailSuccess('Bestätigungsmail gesendet. Bitte prüfen Sie Ihr Postfach.');
                    } catch (err) {
                      if (err instanceof ApiException) {
                        setEmailError(err.data.error);
                      } else {
                        setEmailError('Fehler beim Senden');
                      }
                    } finally {
                      setEmailSaving(false);
                    }
                  }}
                  disabled={emailSaving || !editEmail.trim()}
                  className={`${KNOPF_PRIMAER} inline-flex items-center gap-2`}
                >
                  {emailSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Bestätigungsmail senden
                </button>
              )}
            </>
          }
        >
          {emailError && <Fehlerzeile text={emailError} />}
          {emailSuccess && <Erfolgszeile text={emailSuccess} />}
          {!emailSuccess && (
            <>
              <p className="text-[13px] text-tinte-2">
                Geben Sie Ihre neue E-Mail-Adresse ein. Sie erhalten eine Bestätigungsmail an die
                neue Adresse.
              </p>
              <label className="flex flex-col gap-1.5">
                <span className={FELD_MARKE}>Neue E-Mail-Adresse</span>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  className={EINGABE}
                  placeholder="name@beispiel.de"
                  disabled={emailSaving}
                  autoFocus
                />
              </label>
            </>
          )}
        </Fenster>
      )}

      {/* Profil bearbeiten */}
      {editModalOpen && (
        <Fenster
          titel="Profil bearbeiten"
          gesperrt={saving}
          schliessen={() => setEditModalOpen(false)}
          fuss={
            <>
              <button
                onClick={() => setEditModalOpen(false)}
                className={KNOPF_LEISE}
                disabled={saving}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editDisplayName.trim()}
                className={`${KNOPF_PRIMAER} inline-flex items-center gap-2`}
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Speichern
              </button>
            </>
          }
        >
          {error && <Fehlerzeile text={error} />}
          {success && <Erfolgszeile text={success} />}

          <label className="flex flex-col gap-1.5">
            <span className={FELD_MARKE}>Anzeigename *</span>
            <input
              type="text"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
              className={EINGABE}
              placeholder="Max Mustermann"
              maxLength={128}
              disabled={saving}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className={FELD_MARKE}>Vorname</span>
            <input
              type="text"
              value={editFirstName}
              onChange={(e) => setEditFirstName(e.target.value)}
              className={EINGABE}
              placeholder="Max"
              maxLength={64}
              disabled={saving}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className={FELD_MARKE}>Nachname</span>
            <input
              type="text"
              value={editLastName}
              onChange={(e) => setEditLastName(e.target.value)}
              className={EINGABE}
              placeholder="Mustermann"
              maxLength={64}
              disabled={saving}
            />
          </label>
        </Fenster>
      )}

      {/* Zwei-Faktor: Passwort bestätigen */}
      {twoFAModalOpen && (
        <Fenster
          titel={
            twoFAModalOpen === 'disable' ? 'Zwei-Faktor abschalten' : 'Zwei-Faktor neu einrichten'
          }
          gesperrt={twoFASaving}
          schliessen={() => setTwoFAModalOpen(null)}
          fuss={
            <>
              <button
                onClick={() => setTwoFAModalOpen(null)}
                className={KNOPF_LEISE}
                disabled={twoFASaving}
              >
                Abbrechen
              </button>
              <button
                id="2fa-confirm-btn"
                onClick={async () => {
                  setTwoFAError('');
                  setTwoFASaving(true);
                  try {
                    if (twoFAModalOpen === 'disable') {
                      await disable2FA(twoFAPassword);
                      setTwoFAModalOpen(null);
                      await refresh();
                    } else {
                      await reconfigure2FA(twoFAPassword);
                      setTwoFAModalOpen(null);
                      navigate('/2fa/setup?returnTo=/profile');
                    }
                  } catch (err) {
                    if (err instanceof ApiException) {
                      setTwoFAError(err.data.error);
                    } else {
                      setTwoFAError('Fehler bei der Verarbeitung');
                    }
                  } finally {
                    setTwoFASaving(false);
                  }
                }}
                disabled={twoFASaving || !twoFAPassword.trim()}
                className={`${KNOPF_PRIMAER} inline-flex items-center gap-2`}
              >
                {twoFASaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {twoFAModalOpen === 'disable' ? 'Abschalten' : 'Neu einrichten'}
              </button>
            </>
          }
        >
          {twoFAError && <Fehlerzeile text={twoFAError} />}
          <p className="text-[13px] text-tinte-2">
            {twoFAModalOpen === 'disable'
              ? 'Bitte bestätigen Sie mit Ihrem Passwort, dass Sie die Zwei-Faktor-Anmeldung abschalten möchten.'
              : 'Bitte bestätigen Sie mit Ihrem Passwort. Danach wird ein neuer QR-Code erzeugt.'}
          </p>
          <label className="flex flex-col gap-1.5">
            <span className={FELD_MARKE}>Passwort</span>
            <input
              type="password"
              value={twoFAPassword}
              onChange={(e) => setTwoFAPassword(e.target.value)}
              className={EINGABE}
              placeholder="Ihr aktuelles Passwort"
              disabled={twoFASaving}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && twoFAPassword.trim()) {
                  e.preventDefault();
                  document.getElementById('2fa-confirm-btn')?.click();
                }
              }}
            />
          </label>
        </Fenster>
      )}
    </LeitstandRahmen>
  );
}
